import type { RangeFilter } from '@/domain/entities/SearchFilters'

// Vue 2 parity (constants.RANGE_MAX_VALUE). When only the lower bound is set, the empty upper
// slot must hold a value >= the min instead of 0. The backend derives the range's Min from
// Range.Min() (the numeric minimum of the array — see RangeItem.Min in SearchDataModel.cs), so a
// literal 0 in the second slot would become the effective minimum and silently drop the lower
// bound — e.g. "Turnover from $5B" would collapse to "turnover >= 0" and match ~40% more
// contacts than Vue 2. A max equal to the sentinel is treated as "no upper bound".
export const RANGE_MAX_SENTINEL = 5_000_000_000

/**
 * Builds the RangeFilter payload the backend expects from the two combobox values (0 = empty).
 * Guarantees Range.Min()/Range.Max() line up with the modified flags so unset bounds are never
 * applied.
 */
export function buildRangeFilter(min: number, max: number): RangeFilter {
	const isMinModified = min !== 0
	const isMaxModified = max !== 0 && max !== RANGE_MAX_SENTINEL

	if (!isMinModified && !isMaxModified) {
		return { IsModified: false, IsMinModified: false, IsMaxModified: false, Range: [] }
	}

	// Fill an empty upper bound with a sentinel >= min so Range.Min() stays the real floor.
	const upper = isMaxModified ? max : Math.max(RANGE_MAX_SENTINEL, min)

	return {
		IsModified: true,
		IsMinModified: isMinModified,
		IsMaxModified: isMaxModified,
		Range: [min, upper],
	}
}

interface StepRangeOptions {
	min: number
	max: number
	/** The value each position stands for; positions are sent as-is when empty. */
	valueMap?: readonly number[]
	inverted?: boolean
}

/**
 * A step slider's value, in the shape Vue 2's RangeSteps sends: Range holds the values of the two
 * positions, RangeFixed the positions themselves, IsInverted says the values run high to low. A
 * bound left at its end of the slider is not modified, so the backend ignores it.
 */
export function buildStepRange(positions: [number, number], options: StepRangeOptions): RangeFilter {
	const [from, to] = positions
	const valueAt = (position: number) =>
		options.valueMap && position >= 0 && position < options.valueMap.length
			? options.valueMap[position]
			: position
	const isMinModified = from !== options.min
	const isMaxModified = to !== options.max
	return {
		IsModified: isMinModified || isMaxModified,
		IsMinModified: isMinModified,
		IsMaxModified: isMaxModified,
		IsInverted: options.inverted ?? false,
		RangeFixed: [from, to],
		Range: [valueAt(from), valueAt(to)],
	}
}

/** The slider position whose value is closest to `value`. */
export function stepPositionOf(value: number, valueMap: readonly number[]): number {
	let best = 0
	valueMap.forEach((v, i) => {
		if (Math.abs(v - value) < Math.abs(valueMap[best] - value)) best = i
	})
	return best
}

